import '@/features/trainings/styles/train.css';
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, AlertBanner, sparkToast, sparkAssetUrl } from '@spark';
import { data_from_server_post, get_base_url } from '@/shared/http/client';
import { data_from_server_get } from '@/shared/http/client';
import { taskUrlForUsecase } from '@/features/tasks/utils/navigation';
import { EmptyStateCard } from '@/features/tasks/components/HomePage/EmptyStateCard';
import TrainingsGroupedView from './TrainingsGroupedView.jsx';
import {
    modelDrawerDownloadBenchmark,
    modelDrawerUndeploy,
} from './modelDrawerTrainActions.js';
import { submitConfigureDeployment } from '@/features/deployments/components/configureDeployment/submitConfigureDeployment';
import { buildDefaultDeployPayload } from '@/features/deployments/components/configureDeployment/buildDefaultDeployPayload';
import {
    ConfigureDeploymentFlowModals,
    useConfigureDeploymentFlow,
} from '@/features/deployments';
import TrainingsFilterBar from './TrainingsFilterBar.jsx';
import { groupTrainingsByProject, filterTrainings, getTrainingTaskDisplayName } from './trainingsUtils.js';
import { GlobalContext } from '@/app/providers/globalState';
import { deploy_in_progress, undeploy_in_progress, deployed, ready_to_deploy } from '@/shared/constants/taskStatuses';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import { decidedBaseModelName } from '@/features/trainings/api/baseModelsApi';

export const Trainings = (props) => {
    const { setProgressStatus, allUsecases } = useContext(GlobalContext);
    const navigate = useNavigate();

    const [trainings, setTrainings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modelsData, setModelsData] = useState([]);
    const [failedTrainings, setFailedTrainings] = useState([]);
    const [alertDismissed, setAlertDismissed] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [projectFilter, setProjectFilter] = useState('All Projects');
    const [statusFilter, setStatusFilter] = useState([]);
    const [userFilter, setUserFilter] = useState('All Users');
    const [taskTypeFilter, setTaskTypeFilter] = useState('All');
    const [selectedTraining, setSelectedTraining] = useState(null);
    const [showDeleteNotification, setShowDeleteNotification] = useState(false);
    const [deletedTraining, setDeletedTraining] = useState(null);

    const getUsecaseById = useCallback((usecaseId) => {
        for (let i in allUsecases) {
            if (allUsecases[i]["_id"] === usecaseId) {
                return allUsecases[i]
            }
        }
        return null
    }, [allUsecases]);

    const getModelsData = useCallback(async (successFunction) => {
        data_from_server_get(
            "base-distilled-models",
            (result) => {
                setModelsData(result);
                if (successFunction) {
                    successFunction();
                }
            }
        );
    }, []);

    const get_trainings = useCallback(async () => {
        const origin = window.location.origin
        const url = `${get_base_url(origin)}/trainings`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ "value": DEFAULT_CLIENT_ID }),
        });
        if (!response.ok) {
            console.log(`Error getting trainings: ${response.status} ${response.statusText}`);
        }

        const responseJson = await response.json();

        // Group trainings by usecase to determine which version is being deployed/undeployed
        const trainingsByUsecase = {};
        responseJson.trainings.forEach(v => {
            const usecaseId = v["llm_usecase_id"];
            if (!trainingsByUsecase[usecaseId]) {
                trainingsByUsecase[usecaseId] = [];
            }
            trainingsByUsecase[usecaseId].push(v);
        });

        let trainings = responseJson.trainings.map((v, index) => {
            const usecase = getUsecaseById(v["llm_usecase_id"])
            const usecaseStatus = usecase ? usecase["status"] : null;
            const deployedVersion = usecase ? usecase["deployed_version"] : null;
            const currentVersion = v.version || 1;

            // Determine which version is being deployed/undeployed based on status
            let deployingVersion = undefined;
            let undeployingVersion = undefined;

            if (usecaseStatus === deploy_in_progress) {
                // First, check localStorage for the deploying version (set when user clicked deploy)
                const storageKey = `deploying_${v["llm_usecase_id"]}`;
                const storedDeployingVersion = localStorage.getItem(storageKey);
                if (storedDeployingVersion) {
                    const versionNum = parseFloat(storedDeployingVersion);
                    if (versionNum === currentVersion) {
                        deployingVersion = currentVersion;
                    }
                } else {
                    // Fallback: if no stored version, use heuristic (highest candidate)
                    const usecaseTrainings = trainingsByUsecase[v["llm_usecase_id"]] || [];
                    const completedTrainings = usecaseTrainings.filter(t =>
                        (t.status || "").toLowerCase() === "completed"
                    );

                    const candidateVersions = deployedVersion
                        ? completedTrainings.filter(t => (t.version || 1) > deployedVersion)
                        : completedTrainings;

                    if (candidateVersions.length > 0) {
                        candidateVersions.sort((a, b) => (b.version || 1) - (a.version || 1));
                        if (candidateVersions[0].version === currentVersion) {
                            deployingVersion = currentVersion;
                        }
                    }
                }
            } else if (usecaseStatus === undeploy_in_progress) {
                // First, check localStorage for the undeploying version
                const storageKey = `undeploying_${v["llm_usecase_id"]}`;
                const storedUndeployingVersion = localStorage.getItem(storageKey);
                if (storedUndeployingVersion) {
                    const versionNum = parseFloat(storedUndeployingVersion);
                    if (versionNum === currentVersion) {
                        undeployingVersion = currentVersion;
                    }
                } else {
                    // Fallback: the version being undeployed is the currently deployed version
                    if (deployedVersion === currentVersion) {
                        undeployingVersion = currentVersion;
                    }
                }
            }

            const taskDisplayName = getTrainingTaskDisplayName(usecase);

            return {
                ...v,
                key: index,
                group_name: usecase ? usecase["group_name"] : "Unnamed Project",
                usecase_name: taskDisplayName,
                objective_name: usecase ? usecase["usecase_name"] : "",
                sub_group_name: usecase ? usecase["sub_group_name"] : "",
                usecase_goal: usecase ? usecase["usecase_goal"] : "",
                project_goal: usecase ? usecase["project_goal"] : "",
                base_model_name: decidedBaseModelName(v.base_model_name) || (usecase?.base_distilled_model ?? ""),
                task_type: usecase ? usecase["task_type"] : null,
                version: currentVersion,
                status: v.status || "unknown",
                progress: v.progress || 0,
                finished_datetime: v.finished_datetime || null,
                deployed_version: deployedVersion,
                is_deployed: usecase ? usecase["is_deployed"] : false,
                usecase_status: usecaseStatus,
                deploying_version: deployingVersion,
                undeploying_version: undeployingVersion,
                client_id: usecase ? usecase["client_id"] : null
            }
        })
        trainings = trainings.filter((props.filterFunction || (() => true)))

        // Check for failed trainings and add them to failedTrainings state
        const failedTrainingsFiltered = trainings
            .filter(training => training.status === 'failed')
            .map(training => ({
                id: training.distillation_event_id,
                error: training.error_details || "Unknown error",
                timestamp: training.created_datetime
            }))
            .filter(training => !failedTrainings.some(t => t.id === training.id));

        if (failedTrainingsFiltered.length > 0) {
            setTrainings(trainings);
            setLoading(false);
            setFailedTrainings(prev => [...prev, ...failedTrainingsFiltered]);
        } else {
            setTrainings(trainings);
            setLoading(false);
        }
    }, [getUsecaseById, props.filterFunction, failedTrainings]);


    const handleDeleteTraining = useCallback((training) => {
        data_from_server_post(
            "delete_training",
            {
                "client_id": "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
                "distillation_event_id": training.distillation_event_id,
            },
            (result) => {
                if (result.success) {
                    setDeletedTraining(training);
                    setShowDeleteNotification(true);
                    get_trainings();
                } else {
                    sparkToast.error('Delete Failed', {
                        content: result.message,
                        duration: 6000,
                    });
                }
            }
        );
    }, [get_trainings]);

    const applyDeployOptimistic = useCallback((training) => {
        setTrainings(prevTrainings =>
            prevTrainings.map(t => {
                if (t.llm_usecase_id === training.llm_usecase_id && t.version === training.version) {
                    return {
                        ...t,
                        usecase_status: deploy_in_progress,
                        deploying_version: training.version,
                    };
                }
                return t;
            })
        );
    }, []);

    const handleDeployTraining = useCallback(async (training) => {
        const usecase = getUsecaseById(training.llm_usecase_id);
        try {
            const { mode, sharedConfig, tasks } = buildDefaultDeployPayload(training, usecase);
            await submitConfigureDeployment({
                mode,
                sharedConfig,
                tasks,
                setProgressStatus,
                onDeploySuccess: applyDeployOptimistic,
            });
        } catch (err) {
            sparkToast.error('Deployment Failed', {
                content: err?.message || 'Request failed',
                duration: 6000,
            });
        }
    }, [getUsecaseById, setProgressStatus, applyDeployOptimistic]);

    const handleUndeployTraining = useCallback((training) => {
        modelDrawerUndeploy(training, (_t, versionToUndeploy) => {
            setTrainings(prevTrainings =>
                prevTrainings.map(t => {
                    if (t.llm_usecase_id === training.llm_usecase_id && t.version === versionToUndeploy) {
                        return {
                            ...t,
                            usecase_status: undeploy_in_progress,
                            undeploying_version: versionToUndeploy,
                        };
                    }
                    return t;
                })
            );
        });
    }, []);

    // Shared "Download model" → "Configure deployment" flow (same hook the
    // Model Details drawer uses) so both surfaces behave identically.
    const configureFlow = useConfigureDeploymentFlow({
        setProgressStatus,
        onDeploySuccess: applyDeployOptimistic,
        trainings,
    });

    const handleDownloadBenchmarkTraining = useCallback(
        (training) => modelDrawerDownloadBenchmark(training),
        [],
    );

    // Handler methods for usecase actions
    const handleDeleteUsecase = useCallback((clientId, usecaseId) => {
        data_from_server_post("delete_llm_usecase", {
            client_id: clientId,
            usecase_id: usecaseId,
        },
            (result) => {
                if (result.success) {
                    sparkToast.success('Task Deleted', {
                        content: result.message,
                        duration: 4000,
                    });
                    get_trainings();
                } else {
                    sparkToast.error('Delete Failed', {
                        content: result.message,
                        duration: 6000,
                    });
                }
            }
        );
    }, [get_trainings]);

    const handleViewEditUsecase = useCallback((usecaseId) => {
        const usecase = getUsecaseById(usecaseId);
        if (!usecase) {
            navigate('/');
            return;
        }
        const url = taskUrlForUsecase(usecase, { objective: usecase._id });
        if (url) navigate(url);
        else navigate('/');
    }, [getUsecaseById, navigate]);

    useEffect(() => {
        getModelsData(get_trainings);
    }, [getModelsData, get_trainings]);

    // Create a dependency string that changes when any usecase status/deployment changes
    const usecasesStatusKey = useMemo(() => {
        if (!allUsecases || allUsecases.length === 0) return '';
        return allUsecases.map(u => `${u._id}:${u.status}:${u.deployed_version}:${u.is_deployed}`).join('|');
    }, [allUsecases]);

    // Sync trainings with allUsecases for real-time WebSocket updates
    useEffect(() => {
        if (trainings.length > 0 && allUsecases && allUsecases.length > 0) {
            setTrainings(prevTrainings =>
                prevTrainings.map(training => {
                    const usecase = getUsecaseById(training.llm_usecase_id);
                    if (usecase) {
                        const newUsecaseStatus = usecase.status;
                        const newDeployedVersion = usecase.deployed_version;
                        const newIsDeployed = usecase.is_deployed;

                        // Clear deploying/undeploying flags when deployment completes
                        // Clear deploying when status changes to deployed (success) or ready_to_deploy (if version matches)
                        const shouldClearDeploying = training.deploying_version &&
                            (newUsecaseStatus === deployed ||
                                (newUsecaseStatus === ready_to_deploy && newDeployedVersion !== training.deploying_version));
                        // Clear undeploying when status changes away from undeploy_in_progress
                        const shouldClearUndeploying = training.undeploying_version &&
                            newUsecaseStatus !== undeploy_in_progress;

                        // Clear localStorage when deployment completes
                        if (shouldClearDeploying) {
                            const storageKey = `deploying_${training.llm_usecase_id}`;
                            localStorage.removeItem(storageKey);
                        }
                        if (shouldClearUndeploying) {
                            const storageKey = `undeploying_${training.llm_usecase_id}`;
                            localStorage.removeItem(storageKey);
                        }

                        return {
                            ...training,
                            deployed_version: newDeployedVersion,
                            is_deployed: newIsDeployed,
                            usecase_status: newUsecaseStatus,
                            deploying_version: shouldClearDeploying ? undefined : training.deploying_version,
                            undeploying_version: shouldClearUndeploying ? undefined : training.undeploying_version
                        };
                    }
                    return training;
                })
            );
        }
    }, [usecasesStatusKey, getUsecaseById]);

    // Filter trainings based on search, project, status, user, and task type filter
    const filteredTrainings = filterTrainings(trainings, searchTerm, projectFilter, statusFilter, userFilter, taskTypeFilter);

    // Group filtered trainings by project
    const groupedTrainings = groupTrainingsByProject(filteredTrainings);

    const hasNoTrainings = !loading && trainings.length === 0;

    return (
        <div className='trainings page-content-shell' style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            paddingTop: 0,
            background: 'var(--semantic-surface-main)',
            ...props.style,
        }}>
            {showDeleteNotification && (
                <div style={{ position: 'fixed', left: 0, width: '100%', zIndex: 1100, top: 60 }}>
                    <AlertBanner
                        feedback="success"
                        text={
                            deletedTraining
                                ? `${deletedTraining.usecase_name || 'Model'} was ${deletedTraining.status?.toLowerCase() === 'running' ||
                                    deletedTraining.status?.toLowerCase() === 'waiting' ||
                                    deletedTraining.status?.toLowerCase() === 'preparing' ||
                                    deletedTraining.status?.toLowerCase() === 'training' ||
                                    deletedTraining.status?.toLowerCase() === 'processing'
                                    ? 'stopped'
                                    : 'deleted'
                                } successfully`
                                : 'Model was deleted successfully'
                        }
                        showButton={false}
                        showCloseButton={true}
                        onCloseClick={() => setShowDeleteNotification(false)}
                    />
                </div>
            )}
            <div className="fs-unmask" style={{ padding: '24px 0 32px 0' }}>
                <Header
                    title="Models"
                    subtitle="View and manage your models. View details, training progress, and download."
                    showBreadcrumb={false}
                    showActionBar={false}
                />
            </div>

                {hasNoTrainings ? (
                    <div style={{ padding: '32px 0' }}>
                        <EmptyStateCard
                            onCreateProject={() => navigate('/')}
                            title="No models found"
                            description="After training a model, it will appear here."
                            illustrationSrc={sparkAssetUrl('illustrations/illustration-box-open.svg')}
                            showPrimaryAction={false}
                        />
                    </div>
                ) : (
                    <>
                        <TrainingsFilterBar
                            trainings={trainings}
                            searchTerm={searchTerm}
                            projectFilter={projectFilter}
                            statusFilter={statusFilter}
                            userFilter={userFilter}
                            taskTypeFilter={taskTypeFilter}
                            onSearchChange={setSearchTerm}
                            onProjectFilterChange={setProjectFilter}
                            onStatusFilterChange={setStatusFilter}
                            onUserFilterChange={setUserFilter}
                            onTaskTypeFilterChange={setTaskTypeFilter}
                        />

                        <div style={{ padding: '32px 0' }}>
                            <TrainingsGroupedView
                                groupedTrainings={groupedTrainings}
                                onDeleteTraining={handleDeleteTraining}
                                onDownloadTraining={configureFlow.handleDownloadTraining}
                                onPushModelTraining={configureFlow.handlePushTraining}
                                onDownloadBenchmarkTraining={handleDownloadBenchmarkTraining}
                                onDeployTraining={handleDeployTraining}
                                onUndeployTraining={handleUndeployTraining}
                                onDeleteUsecase={handleDeleteUsecase}
                                onViewEditUsecase={handleViewEditUsecase}
                                loading={loading}
                            />
                        </div>
                    </>
                )}
            <ConfigureDeploymentFlowModals
                configureFlow={configureFlow}
                allUsecases={allUsecases}
            />
        </div>
    );
};

